import React, { useCallback, useMemo, useState } from "react";

import styles from "../../OrgSelector/OrgSelector.module.css";
import {
  AHIcon,
  AHSeparator,
  AHText,
  Box,
  TouchableBox,
  useLayoutContext,
  useTheme,
} from "arkh";
import { graphql, useFragment, useMutation } from "react-relay";

import * as Select from "@radix-ui/react-select";

import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { FormattedMessage, useIntl } from "react-intl";
import { useAlert } from "../../../../common/Alert";
import { MoreButton } from "../../../../common/MoreButton";

import cuid from "cuid";
import { SignalWorkspaceTemplateSelector_workspace$key } from "./__generated__/SignalWorkspaceTemplateSelector_workspace.graphql";
import { SignalWorkspaceTemplateSelector_SignalWorkspace$key } from "./__generated__/SignalWorkspaceTemplateSelector_SignalWorkspace.graphql";
import { SignalWorkspaceTemplateSelectorDeleteTemplateMutation } from "./__generated__/SignalWorkspaceTemplateSelectorDeleteTemplateMutation.graphql";
import { SignalWorkspaceTemplateSelectorApplyTemplateMutation } from "./__generated__/SignalWorkspaceTemplateSelectorApplyTemplateMutation.graphql";
import { SignalWorkspaceTemplateSelectorTemplateItem_SignalWorkspaceTemplate$key } from "./__generated__/SignalWorkspaceTemplateSelectorTemplateItem_SignalWorkspaceTemplate.graphql";
import { SignalWorkspaceTemplateSelectorCreateTemplatePopup } from "./SignalWorkspaceTemplateSelectorCreateTemplatePopup";
import { SignalWorkspaceTemplateSelectorUpdateTemplatePopup } from "./SignalWorkspaceTemplateSelectorUpdateTemplatePopup";

export const SignalWorkspaceTemplateSelector = (props: {
  workspace: SignalWorkspaceTemplateSelector_workspace$key;
  signalWorkspace: SignalWorkspaceTemplateSelector_SignalWorkspace$key;
}) => {
  const signalWorkspace = useFragment(
    graphql`
      fragment SignalWorkspaceTemplateSelector_SignalWorkspace on SignalWorkspace {
        id
        updatedBy {
          __typename
        }
        body

        title

        descriptionFiles {
          __typename
        }
        descriptionImages {
          __typename
        }

        template {
          id
          name
          ...SignalWorkspaceTemplateSelectorTemplateItem_SignalWorkspaceTemplate
        }
      }
    `,
    props.signalWorkspace
  );

  const isEmpty = useMemo(
    () =>
      (signalWorkspace.body === null ||
        signalWorkspace.body.trim().length === 0) &&
      signalWorkspace.descriptionFiles.length === 0 &&
      signalWorkspace.descriptionImages.length === 0,
    [
      signalWorkspace.body,
      signalWorkspace.descriptionFiles.length,
      signalWorkspace.descriptionImages.length,
    ]
  );

  const workspace = useFragment(
    graphql`
      fragment SignalWorkspaceTemplateSelector_workspace on Workspace {
        id
        signalTemplates {
          id
          ...SignalWorkspaceTemplateSelectorTemplateItem_SignalWorkspaceTemplate
        }
      }
    `,
    props.workspace
  );

  const [applyTemplate] =
    useMutation<SignalWorkspaceTemplateSelectorApplyTemplateMutation>(graphql`
      mutation SignalWorkspaceTemplateSelectorApplyTemplateMutation(
        $signalWorkspaceId: ID!
        $templateId: ID!
      ) {
        signalWorkspace(id: $signalWorkspaceId) {
          applyTemplate(templateId: $templateId) {
            id
            ...SignalWorkspaceTemplateSelector_SignalWorkspace
            ...WorkspaceSignalEditMainPrioritySelector_Signal
            ...WorkspaceSignalEditMainEditedInfo_Signal
            ...WorkspaceSignalEditMainContent_Signal
          }
        }
      }
    `);

  const [deleteTemplate] =
    useMutation<SignalWorkspaceTemplateSelectorDeleteTemplateMutation>(graphql`
      mutation SignalWorkspaceTemplateSelectorDeleteTemplateMutation(
        $templateId: ID!
      ) {
        signalWorkspaceTemplate(id: $templateId) {
          deleteTemplate {
            id @deleteRecord
          }
        }
      }
    `);

  const layout = useLayoutContext();
  const theme = useTheme();

  const Alert = useAlert();
  const intl = useIntl();

  const intl_tokens = useIntlTokens();

  const requestApplyTemplate = useCallback(
    (templateId: string) => {
      if (isEmpty) {
        applyTemplate({
          variables: {
            signalWorkspaceId: signalWorkspace.id,
            templateId,
          },
        });
      } else {
        Alert.alert(
          intl_tokens.ApplyTemplate,
          intl.formatMessage({
            defaultMessage:
              "Do you want to replace your current content with the selected template, including description, files and images?",
            description: "Workspace Signal Edit Apply Template Body",
          }),
          [
            {
              text: intl_tokens.Cancel,
              style: "cancel",
            },
            {
              text: intl_tokens.ApplyTemplate,
              style: "default",
              onPress: () => {
                applyTemplate({
                  variables: {
                    signalWorkspaceId: signalWorkspace.id,
                    templateId,
                  },
                });
              },
            },
          ]
        );
      }
    },
    [
      Alert,
      applyTemplate,
      intl,
      intl_tokens.ApplyTemplate,
      intl_tokens.Cancel,
      isEmpty,
      signalWorkspace.id,
    ]
  );

  const requestDeleteTemplate = useCallback(
    (templateId: string, templateName: string) => {
      Alert.alert(
        intl_tokens.DeleteTemplate,
        intl.formatMessage(
          {
            defaultMessage:
              "Do you want to delete the current template {templateName}? This action cannot be undone.",
            description: "Workspace Signal Edit Delete Template Body",
          },
          {
            templateName,
          }
        ),
        [
          {
            text: intl_tokens.Cancel,
            style: "cancel",
          },
          {
            text: intl_tokens.Delete,
            style: "destructive",
            onPress: () => {
              deleteTemplate({
                variables: {
                  templateId,
                },
              });
            },
          },
        ]
      );
    },
    [
      Alert,
      deleteTemplate,
      intl,
      intl_tokens.Cancel,
      intl_tokens.Delete,
      intl_tokens.DeleteTemplate,
    ]
  );

  const [requestingCreateTemplate, setRequestingCreateTemplate] = useState<
    null | string
  >(null);

  const [requestingUpdateTemplate, setRequestingUpdateTemplate] = useState<{
    id: string;
    name: string;
  } | null>(null);

  return (
    <>
      <SignalWorkspaceTemplateSelectorCreateTemplatePopup
        templateName={requestingCreateTemplate}
        setTemplateName={setRequestingCreateTemplate}
        signalWorkspaceId={signalWorkspace.id}
      />
      <SignalWorkspaceTemplateSelectorUpdateTemplatePopup
        template={requestingUpdateTemplate}
        signalWorkspaceId={signalWorkspace.id}
        onDismiss={() => {
          setRequestingUpdateTemplate(null);
        }}
      />
      <Select.Root
        value={signalWorkspace.template?.id ?? cuid()}
        onValueChange={(v) => {
          if (v === "CREATE_NEW") {
            setRequestingCreateTemplate(signalWorkspace.title);
          } else {
            requestApplyTemplate(v);
          }
        }}
      >
        <Select.Trigger
          className={styles.Trigger}
          style={{
            cursor: "pointer",

            border: `1px solid ${theme["border-primary"]}`,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: layout.radius.medium,
            paddingLeft: layout.space.small,
            paddingRight: layout.space.small,
            lineHeight: 1,
            height: 30,
          }}
        >
          <Select.Value>
            {signalWorkspace.template ? (
              <SelectedTemplate title={signalWorkspace.template.name} />
            ) : null}
          </Select.Value>
          {signalWorkspace.template ? null : <NoSelectedTemplate />}
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            position="popper"
            style={{
              cursor: "pointer",
              overflow: "hidden",
              backgroundColor: theme.surface,
              borderRadius: layout.radius.medium,
              zIndex: 99999,
              boxShadow:
                "0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2)",
            }}
          >
            <Select.ScrollUpButton />
            <Select.Viewport>
              {workspace.signalTemplates
                .filter((f) => f?.id)
                .map((template) => (
                  <SignalWorkspaceTemplateItem
                    isSelected={signalWorkspace.template?.id === template.id}
                    template={template}
                    key={template.id}
                  />
                ))}
              <AHSeparator />

              <Select.Item
                value={"CREATE_NEW"}
                className={styles.SelectItem}
                style={{
                  fontSize: 13,
                  lineHeight: 1,

                  borderRadius: 3,
                  display: "flex",
                  alignItems: "center",
                  padding: "15px 20px 15px 20px",
                  position: "relative",
                  userSelect: "none",
                }}
              >
                <Box style={{ flex: 1 }}>
                  <Box
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flex: 1,
                    }}
                  >
                    <AHText spaceRight="large">
                      <FormattedMessage
                        description="Workspace Signal Edit Create Template"
                        defaultMessage="Create new template from current content"
                      />
                    </AHText>
                  </Box>
                </Box>
              </Select.Item>
            </Select.Viewport>
            <Select.ScrollDownButton />
          </Select.Content>
        </Select.Portal>
      </Select.Root>
      <Box spaceLeft="small">
        {signalWorkspace.template ? (
          <MoreButton
            items={[
              {
                icon: "save-replace",
                label: intl_tokens.UpdateTemplate,
                onPress: () => {
                  if (signalWorkspace.template?.id) {
                    setRequestingUpdateTemplate({
                      id: signalWorkspace.template.id,
                      name: signalWorkspace.template.name,
                    });
                  }
                },
              },
              {
                icon: "trash",
                label: intl_tokens.DeleteTemplate,
                color: "negative",
                onPress: () => {
                  if (signalWorkspace.template?.id) {
                    requestDeleteTemplate(
                      signalWorkspace.template.id,
                      signalWorkspace.template.name
                    );
                  }
                },
              },
            ]}
          >
            {(op) => (
              <TouchableBox onPress={op}>
                <AHIcon type="more-vertical" color="text-secondary" />
              </TouchableBox>
            )}
          </MoreButton>
        ) : null}
      </Box>
    </>
  );
};

function NoSelectedTemplate() {
  const intl_tokens = useIntlTokens();
  return (
    <Box style={{ flex: 1, flexDirection: "row", alignItems: "center" }}>
      <AHIcon type="template" color="text-secondary" />
      <AHText spaceLeft="xsmall" type="subtitle" color="text-secondary">
        {intl_tokens.Templates}
      </AHText>
    </Box>
  );
}

function SelectedTemplate(props: { title: string }) {
  return (
    <Box style={{ flex: 1, flexDirection: "row", alignItems: "center" }}>
      <AHIcon type="template" color="text-secondary" />
      <AHText spaceLeft="xsmall" type="subtitle" color="text-secondary">
        {props.title || " - "}
      </AHText>
    </Box>
  );
}

function SignalWorkspaceTemplateItem(props: {
  template: SignalWorkspaceTemplateSelectorTemplateItem_SignalWorkspaceTemplate$key;
  isSelected?: boolean;
}) {
  const template = useFragment(
    graphql`
      fragment SignalWorkspaceTemplateSelectorTemplateItem_SignalWorkspaceTemplate on SignalWorkspaceTemplate {
        id
        name
      }
    `,
    props.template
  );

  return (
    <Select.Item
      value={template.id}
      className={styles.SelectItem}
      style={{
        fontSize: 13,
        lineHeight: 1,

        borderRadius: 3,
        display: "flex",
        alignItems: "center",
        padding: "15px 20px 15px 20px",
        position: "relative",
        userSelect: "none",
      }}
    >
      <Box style={{ flex: 1 }}>
        <Box
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            flex: 1,
          }}
        >
          <Select.ItemText>
            <AHText
              type={props.isSelected ? "subtitle" : "body"}
              color="text-primary"
              spaceRight="large"
            >
              {template.name || " - "}
            </AHText>
          </Select.ItemText>
        </Box>
      </Box>
    </Select.Item>
  );
}
